/* ==========================================================================
   BED&BLOOM THEME COMMERCE & UI CONTROLLER
   ========================================================================== */

(() => {
  'use strict';

  // ------------------------------------------------------------------------
  // Utilities
  // ------------------------------------------------------------------------
  function formatMoney(cents) {
    const amount = Number(cents) / 100;
    const currency = window.BedAndBloom?.currency || 'AED';
    const locale = document.documentElement.lang || 'en';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
    } catch {
      return `${amount.toFixed(2)} ${currency}`;
    }
  }

  function safeHtml(value) {
    return String(value ?? '').replace(/[&<>"']/g, char => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[char]));
  }

  function showToast(message) {
    const toast = document.getElementById('toast');
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('visible');
    clearTimeout(toast._timeout);
    toast._timeout = setTimeout(() => toast.classList.remove('visible'), 3200);
  }

  // ------------------------------------------------------------------------
  // Header & Navigation Controller
  // ------------------------------------------------------------------------
  const header = document.getElementById('header');
  function checkHeaderScroll() {
    if (!header) return;
    if (window.scrollY > 30) {
      header.classList.add('scrolled');
    } else {
      header.classList.remove('scrolled');
    }
  }
  window.addEventListener('scroll', checkHeaderScroll, { passive: true });
  checkHeaderScroll();

  // Mega Menus
  const megaTriggers = document.querySelectorAll('.mega-trigger');
  const megaPanels = document.querySelectorAll('.mega-panel');
  const navScrim = document.getElementById('navigation-scrim');

  function closeAllMega() {
    megaPanels.forEach(panel => (panel.hidden = true));
    megaTriggers.forEach(t => t.setAttribute('aria-expanded', 'false'));
    if (navScrim) navScrim.hidden = true;
  }

  megaTriggers.forEach(trigger => {
    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      const megaId = `mega-${trigger.dataset.mega}`;
      const panel = document.getElementById(megaId);
      if (!panel) return;

      const isOpen = !panel.hidden;
      closeAllMega();
      if (!isOpen) {
        panel.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        if (navScrim) navScrim.hidden = false;
      }
    });
  });

  document.querySelectorAll('.mega-close').forEach(btn => {
    btn.addEventListener('click', closeAllMega);
  });

  if (navScrim) {
    navScrim.addEventListener('click', closeAllMega);
  }

  document.addEventListener('click', (e) => {
    if (!e.target.closest('.header')) closeAllMega();
  });

  // Mobile Navigation Drawer
  const mobileMenuTrigger = document.getElementById('mobile-menu-trigger');
  const mobileNav = document.getElementById('mobile-navigation');
  const mobileNavClose = document.getElementById('mobile-nav-close');

  if (mobileMenuTrigger && mobileNav) {
    mobileMenuTrigger.addEventListener('click', () => {
      mobileNav.showModal();
      document.body.classList.add('drawer-open');
    });
    if (mobileNavClose) {
      mobileNavClose.addEventListener('click', () => {
        mobileNav.close();
        document.body.classList.remove('drawer-open');
      });
    }
    mobileNav.addEventListener('close', () => {
      document.body.classList.remove('drawer-open');
    });
  }

  // ------------------------------------------------------------------------
  // Cart Drawer & Ajax Commerce
  // ------------------------------------------------------------------------
  const cartDrawer = document.getElementById('cart-drawer');
  const cartTriggers = document.querySelectorAll('[data-open="cart"]');

  function openCartDrawer() {
    if (!cartDrawer) return;
    cartDrawer.classList.add('is-open');
    cartDrawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('drawer-open');
    fetchCartAndRender();
  }

  function closeCartDrawer() {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('is-open');
    cartDrawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('drawer-open');
  }

  cartTriggers.forEach(btn => btn.addEventListener('click', openCartDrawer));
  document.addEventListener('click', (event) => { if (event.target.closest('[data-close-drawer]')) closeCartDrawer(); });

  async function fetchCartAndRender() {
    try {
      const res = await fetch(window.BedAndBloom.routes.cart_url + '.js');
      const cart = await res.json();
      if (!res.ok || !Array.isArray(cart.items)) throw new Error('Cart response invalid');
      updateCartUI(cart);
    } catch (e) {
      console.error('Cart fetch failed', e);
    }
  }

  function updateCartUI(cart) {
    // Update live counts
    document.querySelectorAll('.bag-count, #header-bag-count').forEach(el => {
      el.textContent = cart.item_count;
    });

    const drawerCount = document.querySelector('.drawer-count');
    if (drawerCount) drawerCount.textContent = `(${cart.item_count})`;

    const itemsContainer = document.getElementById('cart-drawer-items');
    const footerContainer = document.getElementById('cart-drawer-footer');

    if (!itemsContainer) return;

    if (cart.item_count === 0) {
      itemsContainer.innerHTML = `
        <div class="cart-empty">
          <div class="cart-empty__icon">
            <svg class="icon icon-cart" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.35"><path d="M5 7h14l1 14H4L5 7Z"/><path d="M8 8V6a4 4 0 0 1 8 0v2"/></svg>
          </div>
          <h3>${window.BedAndBloom.strings.cartEmpty || 'Your bag is empty'}</h3>
          <p>${window.BedAndBloom.strings.cartEmptyDesc || 'Discover pieces crafted for slow mornings and peaceful nights.'}</p>
          <a href="${window.BedAndBloom.routes.root_url}collections/all" class="button button-dark" data-close-drawer>
            <span>${safeHtml(window.BedAndBloom.strings.continueShopping)}</span>
            <span aria-hidden="true">&#8599;</span>
          </a>
        </div>
      `;
      if (footerContainer) footerContainer.style.display = 'none';
      return;
    }

    if (footerContainer) footerContainer.style.display = 'block';

    // Render items list
    let html = '<div class="cart-items-list">';
    cart.items.forEach(item => {
      html += `
        <div class="cart-item" data-line-item-key="${safeHtml(item.key)}">
          <div class="cart-item__media">
            ${item.image ? `<img src="${safeHtml(item.image)}" alt="${safeHtml(item.title)}" width="80" height="95">` : ''}
          </div>
          <div class="cart-item__details">
            <h3 class="cart-item__title"><a href="${safeHtml(item.url)}">${safeHtml(item.product_title)}</a></h3>
            ${item.variant_title ? `<p class="cart-item__variant">${safeHtml(item.variant_title)}</p>` : ''}
            <div class="cart-line-price">${formatMoney(item.final_line_price)}</div>
            <div class="cart-actions">
              <div class="quantity-selector">
                <button type="button" class="qty-btn" data-action="decrease" data-key="${safeHtml(item.key)}" data-qty="${item.quantity - 1}">&minus;</button>
                <span class="qty-display">${item.quantity}</span>
                <button type="button" class="qty-btn" data-action="increase" data-key="${safeHtml(item.key)}" data-qty="${item.quantity + 1}">&plus;</button>
              </div>
              <button type="button" class="cart-item__remove" data-action="remove" data-key="${safeHtml(item.key)}">${safeHtml(window.BedAndBloom.strings.remove)}</button>
            </div>
          </div>
        </div>
      `;
    });
    html += '</div>';
    itemsContainer.innerHTML = html;

    // Subtotal
    const subtotalEl = document.querySelector('.cart-subtotal-price');
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

    // Free shipping threshold bar
    const shippingProgress = document.querySelector('.shipping-progress');
    if (shippingProgress) {
      const threshold = Number(shippingProgress.dataset.threshold || 35000);
      const fill = shippingProgress.querySelector('.shipping-progress__fill');
      const text = shippingProgress.querySelector('.shipping-progress__text');
      const percent = Math.min(100, (cart.total_price / threshold) * 100);
      if (fill) fill.style.width = `${percent}%`;

      if (cart.total_price >= threshold) {
        if (text) text.textContent = 'Congratulations! You have qualified for complimentary UAE delivery.';
      } else {
        const remaining = formatMoney(threshold - cart.total_price);
        if (text) text.textContent = `You are ${remaining} away from complimentary delivery in the UAE.`;
      }
    }

    bindCartItemEvents();
  }

  function bindCartItemEvents() {
    document.querySelectorAll('.cart-item [data-action]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const action = btn.dataset.action;
        const key = btn.dataset.key;
        let qty = 0;
        if (action === 'increase' || action === 'decrease') {
          qty = Number(btn.dataset.qty);
        }

        try {
          const res = await fetch(window.BedAndBloom.routes.cart_change_url + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: key, quantity: qty })
          });
          const updatedCart = await res.json();
          if (!res.ok || !Array.isArray(updatedCart.items)) throw new Error(updatedCart.description || 'Cart update failed');
          updateCartUI(updatedCart);
        } catch (err) {
          showToast(window.BedAndBloom.strings.cartUpdateFailed);
        }
      });
    });
  }

  // ------------------------------------------------------------------------
  // Quick Add Buttons
  // ------------------------------------------------------------------------
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.quick-add-btn');
    if (!btn) return;
    e.preventDefault();

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    btn.disabled = true;
    const originalText = btn.innerHTML;
    btn.innerHTML = '<span>Adding...</span>';

    try {
      const res = await fetch(window.BedAndBloom.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });
      const data = await res.json();
      if (data.status) throw new Error(data.description);

      showToast(window.BedAndBloom.strings.added);
      openCartDrawer();
    } catch (err) {
      showToast(err.message || 'Could not add to bag.');
    } finally {
      btn.disabled = false;
      btn.innerHTML = originalText;
    }
  });

  // ------------------------------------------------------------------------
  // Product Detail Page Controller (Variant Picker, Gallery, Sticky ATC)
  // ------------------------------------------------------------------------
  const productSection = document.querySelector('.product-page');
  if (productSection) {
    const variantsScript = document.querySelector('[id^="product-variants-json-"]');
    const variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];
    const variantInput = document.querySelector('[id^="variant-input-"]');
    const priceWrapper = document.querySelector('[id^="product-price-"]');
    const inventoryWrapper = document.querySelector('[id^="inventory-"]');
    const mainGallery = document.getElementById('product-gallery-main');
    const thumbs = document.querySelectorAll('.gallery-thumb-btn');

    // Thumbnail switching
    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const mediaId = thumb.dataset.mediaId;
        thumbs.forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');

        const slides = mainGallery.querySelectorAll('.product-gallery__slide');
        slides.forEach(s => {
          if (s.dataset.mediaId === mediaId) {
            s.classList.add('is-active');
          } else {
            s.classList.remove('is-active');
          }
        });
      });
    });

    // Variant selections (Buttons & Swatches)
    document.querySelectorAll('.variant-pill-btn, .variant-swatch-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const optionIndex = Number(btn.dataset.optionIndex);
        const value = btn.dataset.value;

        // Toggle active button in group
        const group = btn.closest('.variant-option-group');
        group.querySelectorAll('.variant-pill-btn, .variant-swatch-btn').forEach(b => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');

        const labelVal = document.getElementById(`option-val-${optionIndex}`);
        if (labelVal) labelVal.textContent = value;

        // Find matching variant
        const selectedValues = [];
        document.querySelectorAll('.variant-option-group').forEach((g, idx) => {
          const selected = g.querySelector('.is-selected');
          if (selected) selectedValues[idx] = selected.dataset.value;
        });

        const matched = variants.find(v => {
          return v.options.every((opt, i) => opt === selectedValues[i]);
        });

        if (matched) {
          if (variantInput) variantInput.value = matched.id;

          // Update Price
          if (priceWrapper) {
            const salePrice = priceWrapper.querySelector('.price-item--sale') || priceWrapper.querySelector('.price-item--regular');
            if (salePrice) salePrice.textContent = formatMoney(matched.price);
          }

          // Update Inventory
          if (inventoryWrapper) {
            if (matched.available) {
              inventoryWrapper.innerHTML = `<span class="status-dot in-stock"></span><span>${safeHtml(window.BedAndBloom.strings.inStock)}</span>`;
            } else {
              inventoryWrapper.innerHTML = `<span class="status-dot out-of-stock"></span><span>${safeHtml(window.BedAndBloom.strings.soldOut)}</span>`;
            }
          }

          // Update Add to Cart Button
          const atcBtn = document.querySelector('.product-add-btn');
          const stickyAtcBtn = document.getElementById('sticky-atc-trigger');
          if (atcBtn) {
            atcBtn.disabled = !matched.available;
            atcBtn.querySelector('.btn-text').textContent = matched.available ? window.BedAndBloom.strings.addToCart : window.BedAndBloom.strings.soldOut;
          }
          if (stickyAtcBtn) {
            stickyAtcBtn.disabled = !matched.available;
            stickyAtcBtn.querySelector('span').textContent = matched.available ? window.BedAndBloom.strings.addToCart : window.BedAndBloom.strings.soldOut;
          }

          // Switch gallery image if variant has media
          if (matched.featured_media) {
            const targetThumb = document.querySelector(`.gallery-thumb-btn[data-media-id="${matched.featured_media.id}"]`);
            if (targetThumb) targetThumb.click();
          }

          // Update URL
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.set('variant', matched.id);
          window.history.replaceState({}, '', newUrl);
        } else {
          // An option combination with no matching variant MUST NOT purchase the previous variant.
          if (variantInput) variantInput.value = '';
          if (inventoryWrapper) inventoryWrapper.innerHTML = `<span class="status-dot out-of-stock"></span><span>${safeHtml(window.BedAndBloom.strings.unavailable)}</span>`;
          const atcBtn = document.querySelector('.product-add-btn');
          const stickyAtcBtn = document.getElementById('sticky-atc-trigger');
          if (atcBtn) {
            atcBtn.disabled = true;
            const label = atcBtn.querySelector('.btn-text');
            if (label) label.textContent = window.BedAndBloom.strings.unavailable;
          }
          if (stickyAtcBtn) {
            stickyAtcBtn.disabled = true;
            const label = stickyAtcBtn.querySelector('span');
            if (label) label.textContent = window.BedAndBloom.strings.unavailable;
          }
        }
      });
    });

    // Form submission via Ajax
    const productForm = document.querySelector('.product-form');
    if (productForm) {
      productForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const submitBtn = productForm.querySelector('[name="add"]');
        submitBtn.disabled = true;

        const formData = new FormData(productForm);
        try {
          const res = await fetch(window.BedAndBloom.routes.cart_add_url + '.js', {
            method: 'POST',
            body: formData
          });
          const item = await res.json();
          if (item.status) throw new Error(item.description);

          showToast(window.BedAndBloom.strings.added);
          openCartDrawer();
        } catch (err) {
          showToast(err.message || 'Error adding product.');
        } finally {
          submitBtn.disabled = false;
        }
      });
    }

    // Sticky ATC Scroll Visibility
    const stickyAtcBar = document.getElementById('sticky-atc-bar');
    if (stickyAtcBar && productForm) {
      window.addEventListener('scroll', () => {
        const rect = productForm.getBoundingClientRect();
        if (rect.bottom < 0) {
          stickyAtcBar.classList.add('is-visible');
        } else {
          stickyAtcBar.classList.remove('is-visible');
        }
      }, { passive: true });

      const stickyTrigger = document.getElementById('sticky-atc-trigger');
      if (stickyTrigger) {
        stickyTrigger.addEventListener('click', () => {
          const submitBtn = productForm.querySelector('[name="add"]');
          if (submitBtn) submitBtn.click();
        });
      }
    }
  }

  // ------------------------------------------------------------------------
  // Tabbed Essentials Showroom (Homepage Section 3)
  // ------------------------------------------------------------------------
  const tabButtons = document.querySelectorAll('.collection-tabs .tab-btn');
  tabButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      tabButtons.forEach(b => {
        b.classList.remove('is-active');
        b.setAttribute('aria-selected', 'false');
      });
      btn.classList.add('is-active');
      btn.setAttribute('aria-selected', 'true');

      const targetTab = btn.dataset.tab;
      document.querySelectorAll('.featured .tab-panel').forEach(panel => {
        if (panel.id === `panel-${targetTab}`) {
          panel.classList.add('is-active');
          panel.hidden = false;
          // Re-introduce cards when the shopper changes collections.
          if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            const cards = [...panel.querySelectorAll('[data-reveal="product"]')];
            cards.forEach(card => {
              card.style.setProperty('--reveal-delay', `${cards.indexOf(card) % 4 * 70}ms`);
              card.classList.remove('motion-revealed');
              card.classList.add('motion-pending');
            });
            requestAnimationFrame(() => requestAnimationFrame(() => cards.forEach(card => {
              card.classList.remove('motion-pending');
              card.classList.add('motion-revealed');
            })));
          }
        } else {
          panel.classList.remove('is-active');
          panel.hidden = true;
        }
      });
    });
  });

  // ------------------------------------------------------------------------
  // Build Your Bed Experience Controller
  // ------------------------------------------------------------------------
  const buildBedApp = document.getElementById('build-your-bed-app');
  if (buildBedApp) {
    const selects = buildBedApp.querySelectorAll('.bundle-variant-select');
    const totalEl = document.getElementById('bundle-total-price');
    const bundleAddBtn = document.getElementById('add-bundle-to-cart');

    function calculateBundleTotal() {
      let total = 0;
      let selections = 0;
      selects.forEach(sel => {
        const opt = sel.options[sel.selectedIndex];
        if (opt) {
          const price = Number(opt.dataset.price || 0);
          if (sel.value && !opt.disabled) {
            total += price;
            selections++;
          }
        }
      });
      if (totalEl) totalEl.textContent = formatMoney(total);
      if (bundleAddBtn) bundleAddBtn.disabled = selections === 0;
    }

    selects.forEach(sel => sel.addEventListener('change', calculateBundleTotal));
    calculateBundleTotal();

    if (bundleAddBtn) {
      bundleAddBtn.addEventListener('click', async () => {
        bundleAddBtn.disabled = true;
        bundleAddBtn.textContent = window.BedAndBloom.strings.bundleAdding;

        const itemsToAdd = [];
        selects.forEach(sel => {
          const variantId = sel.value;
          if (variantId && sel.selectedOptions[0] && !sel.selectedOptions[0].disabled) {
            itemsToAdd.push({ id: variantId, quantity: 1 });
          }
        });

        if (!itemsToAdd.length) {
          showToast(window.BedAndBloom.strings.selectVariant);
          bundleAddBtn.disabled = false;
          return;
        }
        try {
          const res = await fetch(window.BedAndBloom.routes.cart_add_url + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: itemsToAdd })
          });
          const data = await res.json();
          if (data.status) throw new Error(data.description);

          showToast(window.BedAndBloom.strings.bundleAdded);
          openCartDrawer();
        } catch (err) {
          showToast(err.message || window.BedAndBloom.strings.bundleError);
        } finally {
          bundleAddBtn.disabled = false;
          bundleAddBtn.innerHTML = `<span>${safeHtml(window.BedAndBloom.strings.addBundle)}</span><span aria-hidden="true">&#8599;</span>`;
        }
      });
    }
  }


  // Cart page quantity controls (native form fallback remains available).
  document.querySelectorAll('.cart-page .qty-btn-page').forEach(button => {
    button.addEventListener('click', async () => {
      if (button.disabled) return;
      const field = button.closest('.quantity-input-group')?.querySelector('input[name="updates[]"]');
      const line = Number(button.dataset.line);
      const quantity = Math.max(0, Number(button.dataset.qty));
      if (!field || !Number.isInteger(line) || !line) return;
      button.disabled = true;
      try {
        const response = await fetch(window.BedAndBloom.routes.cart_change_url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ line, quantity })
        });
        if (!response.ok) throw new Error('Cart quantity update failed');
        window.location.reload();
      } catch (error) {
        showToast(window.BedAndBloom.strings.cartUpdateFailed);
        button.disabled = false;
      }
    });
  });

  // ------------------------------------------------------------------------
  // Search Modal & Predictive Search
  // ------------------------------------------------------------------------
  const searchModal = document.getElementById('search-modal');
  const searchTriggers = document.querySelectorAll('[data-open="search"]');
  const searchCloses = document.querySelectorAll('[data-close-search]');
  const searchInput = document.getElementById('SearchModal-Input');
  const predictiveContainer = document.getElementById('predictive-search-results');

  function openSearch() {
    if (!searchModal) return;
    searchModal.classList.add('is-open');
    searchModal.setAttribute('aria-hidden', 'false');
    if (searchInput) setTimeout(() => searchInput.focus(), 150);
  }

  function closeSearch() {
    if (!searchModal) return;
    searchModal.classList.remove('is-open');
    searchModal.setAttribute('aria-hidden', 'true');
  }

  searchTriggers.forEach(b => b.addEventListener('click', openSearch));
  searchCloses.forEach(b => b.addEventListener('click', closeSearch));

  let searchTimeout = 0;
  if (searchInput && predictiveContainer) {
    searchInput.addEventListener('input', () => {
      clearTimeout(searchTimeout);
      const query = searchInput.value.trim();
      if (query.length < 2) {
        predictiveContainer.innerHTML = '';
        return;
      }

      searchTimeout = setTimeout(async () => {
        try {
          const res = await fetch(`${window.BedAndBloom.routes.predictive_search_url}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=4&section_id=predictive-search`);
          const text = await res.text();
          // Fallback simple search if predictive section not returned
          const jsonRes = await fetch(`${window.BedAndBloom.routes.root_url}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product`);
          const data = await jsonRes.json();
          const prods = data.resources.results.products;
          if (prods && prods.length > 0) {
            let html = '';
            prods.forEach(p => {
              html += `
                <a href="${safeHtml(p.url)}" class="predictive-search-item">
                  <img src="${p.image}" alt="${p.title}" width="50" height="55">
                  <div>
                    <h4>${p.title}</h4>
                    <p>${p.price}</p>
                  </div>
                </a>
              `;
            });
            predictiveContainer.innerHTML = html;
          } else {
            predictiveContainer.innerHTML = `<p class="no-results">${safeHtml(window.BedAndBloom.strings.noResults)}</p>`;
          }
        } catch (e) {
          // ignore error
        }
      }, 250);
    });
  }

  // ------------------------------------------------------------------------
  // Facet Filters Auto-Submit on Form Change
  // ------------------------------------------------------------------------
  const facetForm = document.getElementById('FacetFiltersForm');
  if (facetForm) {
    facetForm.addEventListener('change', () => {
      facetForm.submit();
    });
  }

  const mobileFilterOpen = document.getElementById('mobile-filter-open');
  const mobileFilterClose = document.getElementById('mobile-filter-close');
  const collectionAside = document.getElementById('collection-filters-aside');
  if (mobileFilterOpen && collectionAside) {
    mobileFilterOpen.addEventListener('click', () => {
      collectionAside.classList.add('is-open');
      mobileFilterOpen.setAttribute('aria-expanded', 'true');
    });
    if (mobileFilterClose) {
      mobileFilterClose.addEventListener('click', () => {
        collectionAside.classList.remove('is-open');
        mobileFilterOpen.setAttribute('aria-expanded', 'false');
        mobileFilterOpen.focus();
      });
    }
  }
})();
